import { NavLink } from 'react-router-dom';
import type { Id } from '../../api/types';
import { viewSearch } from './nav-links';
import { VIEWS } from './views';

/**
 * The DESKTOP view switcher: a tab row in the top bar, the shape of Jira's
 * `Backlog · Active sprints · Calendar · Reports · List` row in shot 2.
 *
 * ⛔ `hidden md:flex` — this row does NOT appear on a phone. Below 768px the same six destinations
 * are rendered by `BottomTabBar`, because a tab row at the top of a 390×844 screen is the one place
 * a thumb cannot reach without regripping. Two shapes on purpose; see AppShell.
 *
 * ⚠️ `whitespace-nowrap` on each tab and `min-w-0` on the row: at 768px exactly, six labels plus the
 * project switcher and the identity cluster is the tightest this row ever gets. It scrolls rather
 * than wrapping into a second header row, which would change the height of the chrome.
 *
 * ⭐ The current project rides along in the link, so switching Backlog → Board keeps you on the
 * project you were reading. `page`/`ticket`/`expanded` deliberately do not — see `nav-links.ts`.
 */
export function ViewNav({ projectId }: { projectId: Id | null }) {
  const search = viewSearch(projectId);

  return (
    <nav aria-label="Views" className="hidden min-w-0 overflow-x-auto md:block">
      <ul className="flex items-center gap-0.5">
        {VIEWS.map((view) => (
          <li key={view.path}>
            <NavLink
              to={{ pathname: view.path, search }}
              title={view.description}
              className={({ isActive }) =>
                `flex h-8 items-center gap-1.5 rounded px-2.5 text-sm font-medium whitespace-nowrap ${
                  isActive
                    ? 'bg-[color:var(--color-accent)]/12 text-[color:var(--color-accent)]'
                    : 'text-[color:var(--color-ink-muted)] hover:bg-black/5 hover:text-[color:var(--color-ink)] dark:hover:bg-white/10'
                }`
              }
            >
              <span aria-hidden="true" className="text-xs">
                {view.glyph}
              </span>
              {view.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
